import React, { FC, useState } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { CaretLeftIcon } from '@/atoms/Icons/CaretLeftIcon'
import { CaretRightIcon } from '@/atoms/Icons/CaretRightIcon'
import { PlayIcon } from '@/atoms/Icons/PlayIcon'
import { Image } from '@/atoms/Image'
import { slugs } from '@/constants/slugs'
import { ExperimentVariation } from '@/experimentation'
import { FadeInExperiment } from '@/experimentation/components/FadeInExperiment'
import { BasicCarousel } from '@/molecules/BasicCarousel'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { mergeQueryParameters } from '@/utils/UriBuilder'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { vacationSlugs } from '@/views/PreSales/PreSalesGiveawayView/PreSalesGiveawayView'
import { VacationInfo } from '@/views/PreSales/PreSalesGiveawayView/components/VacationInfo'
import {
  GalleryImage,
  GiveawayPrizeItem,
  SuperFanBenefit,
} from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawayData'
import {
  GButtonSM,
  GParagraphLG,
  GParagraphMD,
  GParagraphSM,
  GResponsiveText,
  GTitleLG,
  GTitleMD,
  GTitleSM,
  GTitleXL,
  GTitleXS,
} from '../Typography'

interface Props {
  scrollToPrizes: () => void
  scrollToProducts: () => void
  prizes?: GiveawayPrizeItem[]
  galleryImages: GalleryImage[]
  superFanBenefits?: SuperFanBenefit[]
  trailerUrl: string
  theatricalSlug: string
  projectName: string
  videoImage?: string
  giveawayValue?: string
}

export const PresaleDetailsTab: FC<Props> = ({
  scrollToPrizes,
  prizes,
  superFanBenefits,
  galleryImages,
  trailerUrl,
  theatricalSlug,
  projectName,
  videoImage,
  giveawayValue,
  scrollToProducts,
}) => {
  const { t } = useTranslate('tickets')
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [loadPlayer, setLoadPlayer] = useState(!videoImage)
  const trailer = vacationSlugs.has(theatricalSlug) ? mergeQueryParameters(trailerUrl, { autoplay: '1' }) : trailerUrl

  const openLightbox = (index: number) => {
    setCurrentImageIndex(index)
    setIsLightboxOpen(true)
  }

  return (
    <FlexColumn className="mb-16 w-full gap-16 px-4 pt-16 md:gap-[72px] md:px-16 md:pb-32 lg:px-24">
      <div className="flex w-full flex-col gap-1">
        <div
          id="youtube"
          style={{ aspectRatio: 16 / 9 }}
          className="relative my-3 w-full max-w-[361px] overflow-hidden rounded-[10px] md:mb-[72px] md:max-w-[530px]"
        >
          {!loadPlayer && videoImage && (
            <div className="cursor-pointer" onClick={() => setLoadPlayer(true)}>
              <Image
                alt="Video player thumbnail"
                src={videoImage}
                quality={100}
                fill
                className="object-cover object-center"
              />
              <div className="absolute left-1/2 top-1/2 flex h-fit w-fit -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white p-4">
                <PlayIcon size={32} color="core-gray-950" />
              </div>
            </div>
          )}
          {loadPlayer && <EmbeddedVideo url={trailer} className="mx-auto !rounded-[10px] md:mx-0" />}
        </div>

        {theatricalSlug === slugs.theLastRodeo && (
          <>
            <GResponsiveText
              color="giveaway-primary"
              className="uppercase"
              MobileTextComponent={GTitleLG}
              DesktopTextComponent={GTitleXL}
            >
              {t('giveawayRodeoTitle', 'The Last Rodeo $350,000 Giveaway')}
            </GResponsiveText>

            <div className="mt-3 grid gap-6">
              <GResponsiveText
                color="giveaway-text-subtle-light"
                MobileTextComponent={GTitleXS}
                DesktopTextComponent={GTitleSM}
              >
                {t('giveawayRodeoDescription', 'Ride Home the Ultimate Prize Package!')}
              </GResponsiveText>
              <GParagraphLG color="giveaway-text-subtle-light">
                {t(
                  'giveawayRodeoExplainer',
                  "To celebrate the upcoming release of The Last Rodeo, we're giving you the chance to win the ultimate adventure package! This incredible giveaway is packed with everything you need for the road, the ranch, and beyond—including a state-of-the-art travel trailer, a powerful Ford F-150 Raptor, and even a freezer full of premium beef. Pre-order your tickets now to see The Last Rodeo in theaters on May 23rd, and you’ll be entered to win this once-in-a-lifetime prize!",
                )}
              </GParagraphLG>
              <GResponsiveText
                color="giveaway-text-subtle-light"
                className="mt-4"
                MobileTextComponent={GTitleXS}
                DesktopTextComponent={GTitleSM}
              >
                {t('giveawayRodeoFooterText', 'Enter today for your chance to win!')}
              </GResponsiveText>
            </div>
          </>
        )}

        {theatricalSlug === slugs.kingOfKings && (
          <>
            <GResponsiveText
              color="giveaway-primary"
              className="uppercase"
              MobileTextComponent={GTitleLG}
              DesktopTextComponent={GTitleXL}
            >
              {t('giveawayKokTitle', 'THE KING OF KINGS $100,000 ULTIMATE EXPERIENCE GIVEAWAY')}
            </GResponsiveText>

            <div className="mt-3 grid gap-6">
              <GResponsiveText
                color="giveaway-text-subtle-light"
                MobileTextComponent={GTitleXS}
                DesktopTextComponent={GTitleSM}
              >
                {t('giveawayKokDescriptionv2', 'Experience the Journey of a Lifetime and Celebrate The King of Kings!')}
              </GResponsiveText>
              <GParagraphLG color="giveaway-text-subtle-light">
                {t(
                  'giveawayKokExplainer',
                  'When you pre-order tickets to see The King of Kings in theaters starting April 11, 2025, you enter for a chance to win the ultimate $100,000 experience, featuring an unforgettable trip, exclusive prizes, and more!',
                )}
              </GParagraphLG>
              <GResponsiveText
                color="giveaway-primary"
                className="mt-4"
                MobileTextComponent={GTitleXS}
                DesktopTextComponent={GTitleSM}
              >
                {t('giveawayTechFooterText', 'Pre-order today for your chance to win!')}
              </GResponsiveText>
            </div>
          </>
        )}

        {theatricalSlug === 'rule-breakers-tech' && (
          <>
            <GResponsiveText
              color="giveaway-primary"
              className="uppercase"
              MobileTextComponent={GTitleLG}
              DesktopTextComponent={GTitleXL}
            >
              {t('giveawayTechTitle', 'Rule Breakers $15,000 Tech Giveaway')}
            </GResponsiveText>

            <div className="mt-3 grid gap-6">
              <GResponsiveText
                color="giveaway-text-subtle-light"
                MobileTextComponent={GTitleXS}
                DesktopTextComponent={GTitleSM}
              >
                {t(
                  'giveawayTechDescription',
                  'Fuel your creativity and innovation with a premium tech bundle designed to inspire the next generation of builders, creators, and visionaries.',
                )}
              </GResponsiveText>
              <GParagraphLG color="giveaway-text-subtle-light">
                {t(
                  'giveawayTechExplainer',
                  "Whether you're passionate about engineering, robotics, or gaming, this prize package equips you with the tools to design, develop, and dream bigger. From powerful computing gear to immersive gaming equipment, it's everything you need to take your skills to the next level.",
                )}
              </GParagraphLG>
              <GResponsiveText
                color="giveaway-primary"
                className="mt-4"
                MobileTextComponent={GTitleXS}
                DesktopTextComponent={GTitleSM}
              >
                {t('giveawayTechFooterText', 'Pre-order today for your chance to win!')}
              </GResponsiveText>
            </div>
          </>
        )}

        {theatricalSlug === slugs.homestead && (
          <>
            <GResponsiveText
              color="giveaway-primary"
              className="mb-4 uppercase"
              MobileTextComponent={GTitleLG}
              DesktopTextComponent={GTitleXL}
            >
              {t('preOrderNow', 'Pre-Order Now. Choose Showtimes Later.')}
            </GResponsiveText>
            <GResponsiveText
              color="brave-subtle"
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphMD}
            >
              <Translate t={t} i18nKey="preOrderNowBodyV6">
                Secure your tickets for Homestead today and automatically{' '}
                <b>enter for a chance to win a $300,000 Instant Homestead, including 1 Bitcoin!</b> This incredible
                prize offers ultimate peace of mind and self-sufficiency.
              </Translate>
            </GResponsiveText>

            <GResponsiveText
              color="brave-subtle"
              className="mt-4"
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphMD}
            >
              <Translate t={t} i18nKey="preOrderNowBodyV2_2">
                Don&apos;t miss out—pre-order now and prepare for an unforgettable adventure!
              </Translate>
            </GResponsiveText>
          </>
        )}

        {vacationSlugs.has(theatricalSlug) && (
          <>
            <GResponsiveText
              color="giveaway-text-primary-light"
              className="uppercase md:block"
              MobileTextComponent={GTitleLG}
              DesktopTextComponent={GTitleXL}
            >
              {t('vactionGiveawayTitle', '{{projectName}} $200,000 Dream Vacation Giveaway', { projectName })}
            </GResponsiveText>

            <div className="mt-3 flex flex-col gap-6">
              <GResponsiveText
                color="giveaway-text-subtle-light"
                className="!font-[550] md:block"
                MobileTextComponent={GTitleXS}
                DesktopTextComponent={GTitleSM}
              >
                {t(
                  'preOrderNowBraveSubtitle',
                  'Experience the adventure of a lifetime and share it with the teacher who inspired you!',
                )}
              </GResponsiveText>

              {theatricalSlug === slugs.braveTheDark && (
                <GResponsiveText
                  color="giveaway-text-subtle-light"
                  className="!font-[400] md:block"
                  MobileTextComponent={GParagraphMD}
                  DesktopTextComponent={GParagraphLG}
                >
                  {t(
                    'preOrderNowVacationDescription',
                    'When you pre-order tickets to see {{projectName}} in theaters starting January 24th, 2025, you enter for a chance to win a $200,000 dream vacation.',
                    { projectName },
                  )}
                </GResponsiveText>
              )}
              {theatricalSlug === slugs.ruleBreakers && (
                <GResponsiveText
                  color="giveaway-text-subtle-light"
                  className="!font-[400] md:block"
                  MobileTextComponent={GParagraphMD}
                  DesktopTextComponent={GParagraphLG}
                >
                  {t(
                    'preOrderNowVacationDescriptionRuleBreakers',
                    'When you pre-order tickets to see {{projectName}} in theaters starting March 7th, 2025, you enter for a chance to win a $200,000 dream vacation.',
                    { projectName },
                  )}
                </GResponsiveText>
              )}

              <GResponsiveText
                color="giveaway-text-subtle-light"
                className="!font-[700] md:block"
                MobileTextComponent={GTitleXS}
                DesktopTextComponent={GTitleSM}
              >
                {t('youAndYourFavoriteTeacherWill', 'You and Your Favorite Teacher Will Each Receive:')}
              </GResponsiveText>

              <ul className="flex list-inside list-disc flex-col px-4">
                <li>
                  <GResponsiveText
                    color="giveaway-text-subtle-light"
                    mobileClassName="!inline md:!hidden !font-[400]"
                    desktopClassName="!font-[400] md:!inline"
                    MobileTextComponent={GParagraphMD}
                    DesktopTextComponent={GParagraphLG}
                  >
                    <Translate i18nKey="vacationPlanningCredit" t={t}>
                      <span className="!font-[700]">$50,000 Vacation Planning Credit:</span> Customize your dream
                      getaway with our premier vacation planning service.
                    </Translate>
                  </GResponsiveText>
                </li>
                <li>
                  <GResponsiveText
                    color="giveaway-text-subtle-light"
                    mobileClassName="!inline md:!hidden !font-[400]"
                    desktopClassName="!font-[400] md:!inline"
                    MobileTextComponent={GParagraphMD}
                    DesktopTextComponent={GParagraphLG}
                  >
                    <Translate i18nKey="chooseToReceiveBitcoinDescription" t={t}>
                      <span className="!font-[700]">$50,000 Cash OR $50,000 in Bitcoin (Your Choice):</span> Choose
                      between receiving $50,000 in cash or $50,000 in Bitcoin. Use it to cover taxes, enhance your
                      experience, or add extra adventures to your trip.
                    </Translate>
                  </GResponsiveText>
                </li>
              </ul>

              <GResponsiveText
                color="giveaway-text-subtle-light"
                className="!font-[400] md:block"
                MobileTextComponent={GParagraphMD}
                DesktopTextComponent={GParagraphLG}
              >
                {t(
                  'dontMissBraveOpportunity',
                  "Don't miss this unique opportunity to embark on an unforgettable journey and give back to the teacher who made a difference in your life. Pre-order your tickets today and step closer to making your dream vacation a reality!",
                )}
              </GResponsiveText>
            </div>
          </>
        )}
      </div>

      {superFanBenefits && superFanBenefits.length > 0 && (
        <section>
          <FlexColumn className="gap-1 pb-6">
            <GResponsiveText
              className="uppercase"
              color="giveaway-primary"
              MobileTextComponent={GTitleMD}
              DesktopTextComponent={GTitleLG}
            >
              {t('superfanBenefitItems', 'Superfan Benefit Items')}
            </GResponsiveText>

            <GResponsiveText
              color="giveaway-subtle"
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphLG}
            >
              {t(
                'superfanBenefitItemsBody',
                'Below are some of the exclusive items available based on the backer pack you choose.',
              )}
            </GResponsiveText>
          </FlexColumn>
          <FlexColumn className="gap-12 md:gap-16">
            {superFanBenefits.map((benefit) => (
              <BenefitCard key={benefit.title} {...benefit} />
            ))}
          </FlexColumn>
        </section>
      )}

      {prizes && (
        <section>
          <GTitleLG color="giveaway-text-primary-light" className="mb-1">
            {t('prizeItemsDetails', 'Prize Items')}
          </GTitleLG>
          {giveawayValue && (
            <GResponsiveText
              color="giveaway-text-subtle-light"
              className="mb-6"
              MobileTextComponent={GParagraphSM}
              DesktopTextComponent={GParagraphMD}
            >
              {t('giveawayValueV2', 'This giveaway package is valued at {{giveawayValue}}.', { giveawayValue })}
            </GResponsiveText>
          )}

          <ul className="ml-5 list-[square]">
            {prizes.slice(0, 9).map((prizeItem, index) => (
              <li key={index} className="my-1 !text-giveaway-text-subtle-light">
                <GParagraphSM>
                  {prizeItem.title} ({prizeItem.value})
                </GParagraphSM>
              </li>
            ))}
          </ul>
          <Button
            className="mt-6 w-full rounded-xl border-2 !border-giveaway-primary-accent py-[14.5px] md:w-fit"
            variant="white"
            outline
            onClick={scrollToPrizes}
          >
            <GButtonSM color="giveaway-primary-accent" className="uppercase">
              {t('showAllGiveawayItems', 'Show all Items')}
            </GButtonSM>
          </Button>
        </section>
      )}

      {theatricalSlug === slugs.braveTheDark && (
        <FadeInExperiment experimentName="brave-vacation-details" defaultValue={false}>
          <ExperimentVariation variation={true}>
            <VacationInfo onScrollToProducts={scrollToProducts} />
          </ExperimentVariation>

          <ExperimentVariation default>{null}</ExperimentVariation>
        </FadeInExperiment>
      )}

      <section className="flex flex-col gap-4">
        <GTitleLG color="giveaway-text-primary-light" className="uppercase">
          {t('showImages', 'Show Images')}
        </GTitleLG>
        <BasicCarousel showGradients containerClassName="!gap-2 !px-2">
          {galleryImages.map((image, index) => (
            <img
              key={image.src}
              src={image.src}
              alt={image.alt}
              className="w-72 rounded-lg"
              onClick={() => openLightbox(index)}
            />
          ))}
        </BasicCarousel>
        {isLightboxOpen && (
          <HomesteadLightBox
            showImages={galleryImages}
            currentImageIndex={currentImageIndex}
            setIsLightboxOpen={setIsLightboxOpen}
            setCurrentImageIndex={setCurrentImageIndex}
          />
        )}
      </section>
    </FlexColumn>
  )
}

interface BonusItemCardProps {
  title: string
  description: string
  imageUrl: string
  disclaimer?: string
  badgeText?: string
  badgeClassName?: string
  alt?: string
}

const BenefitCard: FC<BonusItemCardProps> = ({
  title,
  description,
  imageUrl,
  alt,
  disclaimer,
  badgeText,
  badgeClassName,
}) => (
  <FlexColumn className="max-w-[480px] gap-4 self-center md:self-start">
    <div style={{ aspectRatio: 16 / 9 }} className="relative w-full">
      {badgeText && (
        <div
          className={classNames(
            'absolute right-4 top-4 z-10 rounded-md bg-[#D3D3D6] px-2 pt-0.5 pb-1 !font-[bio-sans] text-xs md:text-sm uppercase font-semibold',
            badgeClassName,
          )}
        >
          {badgeText}
        </div>
      )}
      <Image src={imageUrl} alt={alt} className="rounded-lg object-cover" fill />
    </div>
    <FlexColumn className="gap-1">
      <GResponsiveText
        color="giveaway-text-primary-light"
        MobileTextComponent={GTitleXS}
        DesktopTextComponent={GTitleSM}
      >
        {title}
      </GResponsiveText>
      <GResponsiveText
        color="giveaway-text-subtle-light"
        MobileTextComponent={GParagraphSM}
        DesktopTextComponent={GParagraphMD}
      >
        {description}
      </GResponsiveText>
      {disclaimer && (
        <GResponsiveText
          color="giveaway-text-subtle-light"
          MobileTextComponent={GParagraphSM}
          DesktopTextComponent={GParagraphMD}
        >
          <li className="ml-7 list-[square]">{disclaimer}</li>
        </GResponsiveText>
      )}
    </FlexColumn>
  </FlexColumn>
)

interface LightBoxProps {
  showImages: { src: string; alt: string }[]
  currentImageIndex: number
  setIsLightboxOpen: (isOpen: boolean) => void
  setCurrentImageIndex: (index: number) => void
}

const HomesteadLightBox: FC<LightBoxProps> = ({
  showImages,
  currentImageIndex,
  setIsLightboxOpen,
  setCurrentImageIndex,
}) => {
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  const closeLightbox = () => {
    setIsLightboxOpen(false)
  }

  const handleBackgroundClick = () => {
    closeLightbox()
  }

  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }

  const goToPrevious = () => {
    const newIndex = currentImageIndex === 0 ? showImages.length - 1 : currentImageIndex - 1
    setCurrentImageIndex(newIndex)
  }

  const goToNext = () => {
    const newIndex = currentImageIndex === showImages.length - 1 ? 0 : currentImageIndex + 1
    setCurrentImageIndex(newIndex)
  }

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const swipeDistance = touchStart - touchEnd
    const swipeThreshold = 50

    if (swipeDistance > swipeThreshold) {
      goToNext()
    }

    if (swipeDistance < -swipeThreshold) {
      goToPrevious()
    }

    setTouchStart(null)
    setTouchEnd(null)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75"
      id="lightbox-background"
      onClick={handleBackgroundClick}
    >
      <div
        className="relative"
        onClick={handleContentClick}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <button className="absolute left-1 top-1/2 -translate-y-1/2 text-3xl lg:-left-7" onClick={goToPrevious}>
          <CaretLeftIcon size={24} />
        </button>

        <button className="absolute right-1 top-1/2 -translate-y-1/2 text-3xl lg:-right-7" onClick={goToNext}>
          <CaretRightIcon size={24} />
        </button>

        <img
          src={showImages[currentImageIndex].src}
          alt={showImages[currentImageIndex].alt}
          className="max-h-screen max-w-full rounded-lg shadow-lg lg:max-w-screen-lg"
        />
      </div>
    </div>
  )
}
